import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

export type NotificationType =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "pending"
  | "security"
  | "celebration";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface NotificationPayload {
  type: NotificationType;
  title?: string;
  message: string;
  action?: ToastAction;
  /** Duración en milisegundos. Por defecto 4000 (8000 si hay acción). */
  duration?: number;
}

interface ToastItem extends NotificationPayload {
  id: number;
}

interface ToastContextValue {
  /** API simple compatible con el código existente. */
  showToast: (
    message: string,
    type?: NotificationType,
    action?: ToastAction
  ) => void;
  /** API completa: título + descripción + acción + duración. */
  notify: (payload: NotificationPayload) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastStyle {
  container: string;
  icon: string;
}

const TOAST_STYLES: Record<NotificationType, ToastStyle> = {
  success: {
    container: "border-secondary-200 bg-secondary-100 text-secondary-900",
    icon: "ri-checkbox-circle-fill text-secondary-600",
  },
  warning: {
    container: "border-primary-200 bg-primary-100 text-primary-900",
    icon: "ri-alert-fill text-primary-600",
  },
  error: {
    container: "border-accent-200 bg-accent-100 text-accent-900",
    icon: "ri-close-circle-fill text-accent-600",
  },
  info: {
    container: "border-background-200 bg-background-100 text-foreground-800",
    icon: "ri-information-fill text-primary-500",
  },
  pending: {
    container: "border-secondary-200 bg-secondary-50 text-foreground-800",
    icon: "ri-time-fill text-secondary-600",
  },
  security: {
    container: "border-foreground-300 bg-foreground-950 text-background-50",
    icon: "ri-shield-keyhole-fill text-background-50",
  },
  celebration: {
    container: "border-primary-300 bg-primary-500 text-white",
    icon: "ri-trophy-fill text-white",
  },
};

const DEFAULT_DURATION = 4000;
const ACTION_DURATION = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    (payload: NotificationPayload) => {
      const id = ++idRef.current;
      const duration =
        payload.duration ?? (payload.action ? ACTION_DURATION : DEFAULT_DURATION);
      setToasts((prev) => [...prev, { ...payload, id }]);
      window.setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const showToast = useCallback(
    (message: string, type: NotificationType = "info", action?: ToastAction) => {
      notify({ type, message, action });
    },
    [notify]
  );

  return (
    <ToastContext.Provider value={{ showToast, notify }}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4"
        role="region"
        aria-label="Notificaciones"
      >
        {toasts.map((toast) => {
          const style = TOAST_STYLES[toast.type];
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto flex w-full max-w-md items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${style.container}`}
              role="status"
            >
              <i className={`${style.icon} mt-0.5 text-lg`}></i>
              <div className="min-w-0 flex-1">
                {toast.title && (
                  <p className="font-semibold leading-snug">{toast.title}</p>
                )}
                <p
                  className={`leading-snug ${
                    toast.title ? "mt-0.5 text-[13px] opacity-90" : "font-medium"
                  }`}
                >
                  {toast.message}
                </p>
                {toast.action && (
                  <button
                    type="button"
                    onClick={() => {
                      toast.action?.onClick();
                      removeToast(toast.id);
                    }}
                    className="mt-2 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-foreground-950 px-2.5 py-1.5 text-xs font-semibold text-background-50 transition-opacity hover:opacity-85"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                aria-label="Cerrar notificación"
                className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast debe usarse dentro de <ToastProvider>");
  }
  return context;
}